import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    FlaskConical,
    Plus,
    Barcode,
    Ban,
    Trash2,
    AlertTriangle,
    ArrowRightCircle,
    Truck,
    ScanLine,
    User2,
    CalendarDays,
    ClipboardList,
    CheckCircle2,
    Send,
    XCircle,
    FileText,
    Mail,
    Printer,
    MoreHorizontal,
} from 'lucide-react';
import { toast } from 'react-toastify';
import LabServices from '../../../shared/ApiServices/LabServices';
import { Button, Dialog, Select, Input, TextArea, Dropdown } from '../../../shared/custom-ui';
import useLabPermissions from '../useLabPermissions';
import {
    extractLabResultsList,
    extractLabResultId,
    normalizeResultStatus,
    RESULT_STATUS_META,
    extractLabSampleId,
    normalizeSampleStatus,
    SAMPLE_STATUS_META,
    type SampleStatus,
    extractSampleTypesList,
    formatLabDate,
    type LabOrder,
    type LabSample,
    type LabSampleType,
    type LabResult,
} from '../labUtils';
import { ResultEntryModal } from './ResultEntryModal';

const NEXT_SAMPLE_STATUS: Record<SampleStatus, SampleStatus | null> = {
    pending: 'collected',
    collected: 'in_transit',
    in_transit: 'accessioned',
    accessioned: null,
    rejected: null,
};
const ADVANCE_LABEL: Record<SampleStatus, string> = {
    pending: 'Mark Collected',
    collected: 'Send for Transit',
    in_transit: 'Accession Sample',
    accessioned: '',
    rejected: '',
};
const ADVANCE_ICON: Record<SampleStatus, React.ElementType> = {
    pending: ArrowRightCircle,
    collected: Truck,
    in_transit: ScanLine,
    accessioned: ArrowRightCircle,
    rejected: ArrowRightCircle,
};

const Field: React.FC<{ label: string; required?: boolean; children: React.ReactNode }> = ({
    label,
    required,
    children,
}) => (
    <div className="flex flex-col gap-1.5">
        <label className="text-[12px] font-semibold text-[var(--v2-text-secondary)]">
            {label}
            {required && <span className="ml-0.5 text-[var(--v2-danger)]">*</span>}
        </label>
        {children}
    </div>
);

const RegisterSpecimenModal: React.FC<{
    orderId: string;
    testId: string;
    testName: string;
    sampleTypes: LabSampleType[];
    onClose: () => void;
    onSuccess: () => void;
}> = ({ orderId, testId, testName, sampleTypes, onClose, onSuccess }) => {
    const [form, setForm] = useState({
        sampleTypeName: '',
        bottleType: '',
        collectedMaxQty: '',
        collectedMinQty: '',
        collectedBy: '',
    });
    const [saving, setSaving] = useState(false);

    const handleSampleTypeChange = (typeName: string) => {
        const matched = sampleTypes.find(
            st => (st.sampleTypeName ?? (st as any).sample_type_name) === typeName
        );
        setForm(f => ({
            ...f,
            sampleTypeName: typeName,
            bottleType: matched
                ? ((matched as any).bottleType ?? (matched as any).bottle_type ?? f.bottleType)
                : f.bottleType,
        }));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const res: any = await LabServices.registerSample(orderId, { ...form, testId });
            const barcode = res?.barcodeNumber ?? res?.barcode_number;
            toast.success(
                barcode
                    ? `${res?.message ?? 'Sample registered'} — Barcode: ${barcode}`
                    : (res?.message ?? 'Sample registered')
            );
            onSuccess();
            onClose();
        } catch (err: any) {
            toast.error(err?.message ?? 'Failed');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog
            isOpen
            onClose={onClose}
            title="Register Specimen"
            description={testName}
            size="medium"
            preventCloseOnOverlayClick
        >
            <div className="flex flex-col gap-3.5">
                <Field label="Sample Type">
                    <Select
                        value={form.sampleTypeName}
                        onChange={handleSampleTypeChange}
                        placeholder="— Select type —"
                        options={sampleTypes.map(st => ({
                            value: st.sampleTypeName ?? (st as any).sample_type_name ?? '',
                            label: st.sampleTypeName ?? (st as any).sample_type_name ?? '',
                        }))}
                    />
                </Field>
                <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
                    <Input
                        label="Bottle Type"
                        value={form.bottleType}
                        onChange={e => setForm(f => ({ ...f, bottleType: e.target.value }))}
                        placeholder="e.g. Lavender EDTA Tube"
                    />
                    <Input
                        label="Collected By"
                        value={form.collectedBy}
                        onChange={e => setForm(f => ({ ...f, collectedBy: e.target.value }))}
                        placeholder="Technician name"
                    />
                </div>
                <div className="grid grid-cols-2 gap-3.5">
                    <Input
                        label="Min Qty"
                        value={form.collectedMinQty}
                        onChange={e => setForm(f => ({ ...f, collectedMinQty: e.target.value }))}
                        placeholder="e.g. 3 mL"
                    />
                    <Input
                        label="Max Qty"
                        value={form.collectedMaxQty}
                        onChange={e => setForm(f => ({ ...f, collectedMaxQty: e.target.value }))}
                        placeholder="e.g. 5 mL"
                    />
                </div>
                <div className="flex justify-end gap-3 border-t border-[var(--v2-border-subtle)] pt-3">
                    <Button
                        variant="outline"
                        onClick={onClose}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleSave}
                        loading={saving}
                    >
                        Register
                    </Button>
                </div>
            </div>
        </Dialog>
    );
};

// ─── Reject Sample modal ────────────────────────────────────────────────────────
const RejectSampleModal: React.FC<{
    onConfirm: (reason: string) => void;
    onClose: () => void;
    rejecting: boolean;
}> = ({ onConfirm, onClose, rejecting }) => {
    const [reason, setReason] = useState('');
    return (
        <Dialog
            isOpen
            onClose={onClose}
            size="small"
            preventCloseOnOverlayClick
            showCloseButton={false}
        >
            <div className="-mt-2 flex flex-col gap-4">
                <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-red-100">
                        <Ban
                            className="h-5 w-5 text-red-600"
                            strokeWidth={2.5}
                        />
                    </div>
                    <div>
                        <h3 className="m-0 text-[15px] font-bold text-[var(--v2-text-heading)]">
                            Reject Sample
                        </h3>
                        <p className="m-0 text-[12px] font-medium leading-relaxed text-[var(--v2-text-secondary)]">
                            Record why this specimen failed integrity checks.
                        </p>
                    </div>
                </div>
                <TextArea
                    label="Reason for Rejection"
                    required
                    rows={3}
                    placeholder="e.g. Hemolysed sample, insufficient volume"
                    value={reason}
                    onChange={e => setReason(e.target.value)}
                />
                <div className="flex justify-end gap-3">
                    <Button
                        variant="outline"
                        onClick={onClose}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="danger"
                        onClick={() => onConfirm(reason)}
                        disabled={!reason.trim()}
                        loading={rejecting}
                        leftIcon={
                            <Ban
                                className="h-full w-full"
                                strokeWidth={2}
                            />
                        }
                    >
                        Reject Sample
                    </Button>
                </div>
            </div>
        </Dialog>
    );
};

// ─── Reject Result modal ────────────────────────────────────────────────────────
const RejectResultModal: React.FC<{
    onConfirm: (reason: string) => void;
    onClose: () => void;
    rejecting: boolean;
}> = ({ onConfirm, onClose, rejecting }) => {
    const [reason, setReason] = useState('');
    return (
        <Dialog
            isOpen
            onClose={onClose}
            size="small"
            preventCloseOnOverlayClick
            showCloseButton={false}
        >
            <div className="-mt-2 flex flex-col gap-4">
                <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-red-100">
                        <XCircle
                            className="h-5 w-5 text-red-600"
                            strokeWidth={2.5}
                        />
                    </div>
                    <div>
                        <h3 className="m-0 text-[15px] font-bold text-[var(--v2-text-heading)]">
                            Reject Result
                        </h3>
                        <p className="m-0 text-[12px] font-medium leading-relaxed text-[var(--v2-text-secondary)]">
                            Visible in the audit trail.
                        </p>
                    </div>
                </div>
                <TextArea
                    rows={3}
                    placeholder="State the reason for rejection…"
                    value={reason}
                    onChange={e => setReason(e.target.value)}
                />
                <div className="flex justify-end gap-3">
                    <Button
                        variant="outline"
                        onClick={onClose}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="danger"
                        onClick={() => onConfirm(reason)}
                        disabled={!reason.trim()}
                        loading={rejecting}
                    >
                        Reject
                    </Button>
                </div>
            </div>
        </Dialog>
    );
};

// ─── Barcode modal ─────────────────────────────────────────────────────────────
const BarcodeModal: React.FC<{ sampleId: string; onClose: () => void }> = ({
    sampleId,
    onClose,
}) => {
    const [loading, setLoading] = useState(true);
    const [svgHtml, setSvgHtml] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        (async () => {
            try {
                const res = await LabServices.getSampleBarcode(sampleId);
                const b64 =
                    res?.barcodeSvgBase64 ??
                    res?.barcode_svg_base64 ??
                    res?.svg ??
                    res?.barcodeData ??
                    res?.barcode;
                if (b64) setSvgHtml(atob(b64));
                else if (typeof res === 'string' && res.includes('<svg')) setSvgHtml(res);
                else setError('Barcode data not found in response');
            } catch (err: any) {
                setError(err?.message ?? 'Failed to load barcode');
            } finally {
                setLoading(false);
            }
        })();
    }, [sampleId]);

    return (
        <Dialog
            isOpen
            onClose={onClose}
            title="Specimen Barcode"
            size="small"
        >
            {loading ? (
                <div className="h-40 animate-pulse rounded-xl border border-[var(--v2-border-card)] bg-[var(--v2-bg-surface)]" />
            ) : error ? (
                <div className="flex flex-col items-center gap-2 rounded-xl border border-red-200 bg-red-50 p-4 text-center">
                    <AlertTriangle className="h-6 w-6 text-red-500" />
                    <p className="m-0 text-[13px] font-bold text-red-700">{error}</p>
                </div>
            ) : (
                <div
                    className="flex w-full items-center justify-center rounded-xl border border-[var(--v2-border-card)] bg-white p-6 [&>svg]:h-auto [&>svg]:w-full [&>svg]:max-w-[250px]"
                    dangerouslySetInnerHTML={{ __html: svgHtml }}
                />
            )}
        </Dialog>
    );
};

// ─── Email report modal — order-level, not tied to a single test/result ────────
const EmailModal: React.FC<{ orderId: string; onClose: () => void }> = ({ orderId, onClose }) => {
    const [email, setEmail] = useState('');
    const [sending, setSending] = useState(false);
    const handleSend = async () => {
        if (!email.trim()) {
            toast.error('Enter an email address');
            return;
        }
        setSending(true);
        try {
            const res: any = await LabServices.emailReport(orderId, email.trim());
            toast.success(res?.message ?? 'Report emailed');
            onClose();
        } catch (err: any) {
            toast.error(err?.message ?? 'Failed to send');
        } finally {
            setSending(false);
        }
    };
    return (
        <Dialog
            isOpen
            onClose={onClose}
            title="Email Report"
            size="small"
        >
            <div className="-mt-1 flex flex-col gap-4">
                <Input
                    type="email"
                    placeholder="patient@email.com"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleSend()}
                />
                <div className="flex justify-end gap-3">
                    <Button
                        variant="outline"
                        onClick={onClose}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleSend}
                        loading={sending}
                        leftIcon={
                            !sending ? (
                                <Mail
                                    className="h-full w-full"
                                    strokeWidth={2}
                                />
                            ) : undefined
                        }
                    >
                        Send
                    </Button>
                </div>
            </div>
        </Dialog>
    );
};

interface OrderWorkflowListProps {
    orderId: string;
    order: LabOrder;
    samples: LabSample[];
    onSampleChange: () => void;
}

export const OrderWorkflowList: React.FC<OrderWorkflowListProps> = ({ orderId, order, samples, onSampleChange }) => {

    const {
        canCollectSamples,
        canVerifyResults,
        canApproveReports,
        canDispatchReports,
        currentUser: labUser,
    } = useLabPermissions();

    const [results, setResults] = useState<LabResult[]>([]);
    const [sampleTypes, setSampleTypes] = useState<LabSampleType[]>([]);
    const [loadingResults, setLoadingResults] = useState(true);
    const [generatingPdf, setGeneratingPdf] = useState(false);
    const [printing, setPrinting] = useState(false);
    const [showEmailModal, setShowEmailModal] = useState(false);

    const [registerTarget, setRegisterTarget] = useState<{ testId: string; testName: string } | null>(
        null
    );
    const [resultTarget, setResultTarget] = useState<{
        testId: string;
        testName: string;
        resultId: string | null;
    } | null>(null);
    const [barcodeTarget, setBarcodeTarget] = useState<string | null>(null);
    const [sampleActionsOpen, setSampleActionsOpen] = useState<string | null>(null);
    const [rejectSampleTarget, setRejectSampleTarget] = useState<string | null>(null);
    const [rejectingSample, setRejectingSample] = useState(false);
    const [rejectResultTarget, setRejectResultTarget] = useState<string | null>(null);
    const [rejectingResult, setRejectingResult] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState<LabSample | null>(null);
    const [toggling, setToggling] = useState<string | null>(null);
    const [resultActionBusy, setResultActionBusy] = useState<string | null>(null);
    const mountedRef = useRef(true);

    const loadResults = useCallback(async () => {
        if (!orderId) return;
        setLoadingResults(true);
        try {
            const [r, st] = await Promise.all([
                LabServices.getOrderResults(orderId),
                LabServices.listSampleTypes(),
            ]);
            if (mountedRef.current) {
                setResults(extractLabResultsList(r));
                setSampleTypes(extractSampleTypesList(st));
            }
        } catch (err: any) {
            toast.error(err?.message ?? 'Failed to load results');
        } finally {
            if (mountedRef.current) setLoadingResults(false);
        }
    }, [orderId]);

    useEffect(() => {
        mountedRef.current = true;
        loadResults();
        return () => {
            mountedRef.current = false;
        };
    }, [loadResults]);

    const refreshAll = () => {
        loadResults();
        onSampleChange();
    };

    const advanceStatus = async (sample: LabSample) => {
        const current = normalizeSampleStatus(sample);
        const next = NEXT_SAMPLE_STATUS[current];
        if (!next) return;
        const sId = extractLabSampleId(sample);
        setToggling(sId);
        try {
            const stamp: Record<string, any> = { status: next };
            if (next === 'collected') stamp.isCollected = true;
            if (next === 'in_transit') stamp.inTransitAt = new Date().toISOString();
            if (next === 'accessioned') {
                stamp.accessionedBy = labUser?.name ?? labUser?.id ?? 'Unknown user';
                stamp.accessionedAt = new Date().toISOString();
            }
            const res: any = await LabServices.updateSample(sId, stamp);
            toast.success(res?.message ?? `Marked as ${SAMPLE_STATUS_META[next].label}`);
            refreshAll();
        } catch (err: any) {
            toast.error(err?.message ?? 'Update failed');
        } finally {
            setToggling(null);
        }
    };

    const confirmRejectSample = async (reason: string) => {
        if (!rejectSampleTarget) return;
        setRejectingSample(true);
        try {
            const res: any = await LabServices.updateSample(rejectSampleTarget, {
                isRejected: true,
                rejectionReason: reason,
                status: 'rejected',
            });
            toast.success(res?.message ?? 'Sample rejected');
            setRejectSampleTarget(null);
            refreshAll();
        } catch (err: any) {
            toast.error(err?.message ?? 'Reject failed');
        } finally {
            setRejectingSample(false);
        }
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        const sId = extractLabSampleId(deleteTarget);
        try {
            const res: any = await LabServices.deleteSample(sId);
            toast.success(res?.message ?? 'Sample deleted');
            setDeleteTarget(null);
            refreshAll();
        } catch (err: any) {
            toast.error(err?.message ?? 'Delete failed');
        }
    };

    const handleVerifyResult = async (result: LabResult) => {
        const rId = extractLabResultId(result);
        setResultActionBusy(`${rId}:verify`);
        try {
            const verifiedBy = labUser?.name ?? labUser?.id ?? 'Unknown user';
            const verifiedAt = new Date().toISOString();
            const res: any = await LabServices.updateResult(rId, {
                findings: result.findings,
                impression: result.impression,
                parameters: (result.parameters ?? []).map(p => ({
                    paramName: p.paramName ?? (p as any).param_name ?? '',
                    value: p.value ?? '',
                    unit: p.unit ?? '',
                    referenceRange: p.referenceRange ?? (p as any).reference_range ?? '',
                    criticalLow: p.criticalLow,
                    criticalHigh: p.criticalHigh,
                })),
                verifiedBy,
                verifiedAt,
            });
            toast.success(res?.message ?? 'Result verified');
            setResults(prev =>
                prev.map(r => (extractLabResultId(r) === rId ? { ...r, verifiedBy, verifiedAt } : r))
            );
            onSampleChange();
        } catch (err: any) {
            toast.error(err?.message ?? 'Verify failed');
        } finally {
            setResultActionBusy(null);
        }
    };

    const handleSubmitResult = async (result: LabResult) => {
        const rId = extractLabResultId(result);
        setResultActionBusy(`${rId}:submit`);
        try {
            const res = await LabServices.submitForReview(rId);
            toast.success(res?.message ?? 'Submitted for approval');
            refreshAll();
        } catch (err: any) {
            toast.error(err?.message ?? 'Submit failed');
        } finally {
            setResultActionBusy(null);
        }
    };

    const handleApproveResult = async (result: LabResult) => {
        const rId = extractLabResultId(result);
        setResultActionBusy(`${rId}:approve`);
        try {
            const res = await LabServices.approveReport(rId);
            toast.success(res?.message ?? 'Report approved');
            refreshAll();
        } catch (err: any) {
            toast.error(err?.message ?? 'Approve failed');
        } finally {
            setResultActionBusy(null);
        }
    };

    const confirmRejectResult = async (reason: string) => {
        if (!rejectResultTarget) return;
        setRejectingResult(true);
        try {
            const res: any = await LabServices.rejectReport(rejectResultTarget, reason);
            toast.success(res?.message ?? 'Report rejected');
            setRejectResultTarget(null);
            refreshAll();
        } catch (err: any) {
            toast.error(err?.message ?? 'Reject failed');
        } finally {
            setRejectingResult(false);
        }
    };

    const handleGeneratePdf = async () => {
        if (!orderId) return;
        setGeneratingPdf(true);
        try {
            const genRes: any = await LabServices.generateReportPdf(orderId);
            let openUrl = '';
            try {
                const getRes: any = await LabServices.getReportPdfUrl(orderId);
                openUrl =
                    getRes?.reportUrl ?? getRes?.report_url ?? getRes?.url ?? getRes?.presignedUrl ?? '';
            } catch {
                /* GET failed — handled below */
            }
            const isValidUrl = (u: string) => u.startsWith('http://') || u.startsWith('https://');
            if (openUrl && isValidUrl(openUrl)) {
                window.open(openUrl, '_blank', 'noopener,noreferrer');
                toast.success(genRes?.message ?? 'Report generated — opening in new tab');
            } else {
                const reportKey: string = genRes?.reportKey ?? genRes?.report_key ?? '';
                toast[reportKey ? 'success' : 'info'](
                    reportKey
                        ? `${genRes?.message ?? 'Report generated'} — refresh and try again shortly.`
                        : (genRes?.message ?? 'Report generated. Try opening it again in a moment.')
                );
            }
        } catch (err: any) {
            toast.error(err?.message ?? 'Failed to generate report');
        } finally {
            setGeneratingPdf(false);
        }
    };

    const handlePrint = async () => {
        if (!orderId) return;
        setPrinting(true);
        try {
            const res: any = await LabServices.getReportPdfUrl(orderId);
            const url: string = res?.reportUrl ?? res?.report_url ?? res?.url ?? res?.presignedUrl ?? '';
            if (!url?.startsWith('http')) {
                toast.info('Generate the report first, then try printing.');
                return;
            }
            // Fetch as blob → hidden iframe → print() — avoids popup blocker entirely
            const blob = await fetch(url).then(r => r.blob());
            const blobUrl = URL.createObjectURL(blob);
            const iframe = document.createElement('iframe');
            iframe.style.cssText = 'position:fixed;top:0;left:0;width:0;height:0;border:0;';
            iframe.src = blobUrl;
            document.body.appendChild(iframe);
            iframe.onload = () => {
                iframe.contentWindow?.print();
                setTimeout(() => {
                    document.body.removeChild(iframe);
                    URL.revokeObjectURL(blobUrl);
                }, 1000);
            };
        } catch (err: any) {
            toast.error(err?.message ?? 'Print failed');
        } finally {
            setPrinting(false);
        }
    };

    const tests = order.tests ?? [];

    if (tests.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-[var(--v2-border-card)] bg-[var(--v2-bg-card)] py-16 text-center">
                <div className="flex h-14 w-14 items-center justify-center rounded-full border border-[var(--v2-border-card)] bg-[var(--v2-bg-surface)]">
                    <FlaskConical
                        className="h-7 w-7 text-[var(--v2-text-muted)] opacity-50"
                        strokeWidth={1.5}
                    />
                </div>
                <div>
                    <h3 className="m-0 mb-1 text-[15px] font-bold text-[var(--v2-text-heading)]">
                        No Tests Ordered
                    </h3>
                    <p className="m-0 text-[13px] text-[var(--v2-text-muted)]">
                        This order currently does not contain any tests.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-3">
            {/* Report Delivery — order-level, once at least one result is approved */}
            {results.some(
                r => normalizeResultStatus((r as any).reportStatus ?? r.status) === 'approved'
            ) &&
                (canDispatchReports || canApproveReports) && (
                    <div className="flex flex-col justify-between gap-3 rounded-xl border border-[var(--v2-border-card)] bg-[var(--v2-bg-card)] p-4 md:flex-row md:items-center">
                        <div className="flex flex-col">
                            <h4 className="m-0 text-[13px] font-bold text-[var(--v2-text-heading)]">
                                Report Ready for Delivery
                            </h4>
                            <p className="m-0 text-[11px] font-medium text-[var(--v2-text-muted)]">
                                One or more results are approved — the combined order report can be dispatched to
                                the patient.
                            </p>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                            <Button
                                variant="outline"
                                size="small"
                                onClick={handleGeneratePdf}
                                loading={generatingPdf}
                                leftIcon={
                                    !generatingPdf ? <FileText className="h-full w-full text-blue-600" /> : undefined
                                }
                            >
                                View Report
                            </Button>
                            <Button
                                variant="outline"
                                size="small"
                                onClick={() => setShowEmailModal(true)}
                                leftIcon={<Mail className="h-full w-full text-teal-600" />}
                            >
                                Email
                            </Button>
                            <Button
                                variant="outline"
                                size="small"
                                onClick={handlePrint}
                                loading={printing}
                                leftIcon={
                                    !printing ? <Printer className="h-full w-full text-purple-600" /> : undefined
                                }
                            >
                                Print
                            </Button>
                        </div>
                    </div>
                )}

            {tests.map((t, i) => {
                const testId = (t as any).testId ?? (t as any).test_id ?? '';
                const testName = (t as any).testName ?? (t as any).test_name ?? testId;

                const sample = samples.find(sm => (sm.testId ?? (sm as any).test_id) === testId) ?? null;
                const sampleStatus = sample ? normalizeSampleStatus(sample) : null;
                const testResults = results.filter(r => (r.testId ?? (r as any).test_id) === testId);

                const sId = sample ? extractLabSampleId(sample) : '';
                const nextSampleStatus = sampleStatus ? NEXT_SAMPLE_STATUS[sampleStatus] : null;
                const AdvanceIcon = sampleStatus ? ADVANCE_ICON[sampleStatus] : ArrowRightCircle;
                const canEnterResultNow = sampleStatus && sampleStatus !== 'rejected'; // collected/in_transit/accessioned all allowed, warning shown inline + in modal
                const openResultModal = (existingResult: LabResult | null) =>
                    setResultTarget({
                        testId,
                        testName,
                        resultId: existingResult ? extractLabResultId(existingResult) : null,
                    });

                return (
                    <div
                        key={i}
                        className="rounded-xl border border-[var(--v2-border-card)] bg-[var(--v2-bg-card)] shadow-[0_1px_2px_rgba(15,23,42,0.03)]"
                    >
                        {/* ── Test header ── */}
                        <div className="flex items-center gap-3 border-b border-[var(--v2-border-subtle)] px-5 py-3.5">
                            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-[var(--v2-primary-50,#eff6ff)] text-[13px] font-bold text-[var(--v2-primary-600,#045eb8)]">
                                {i + 1}
                            </div>
                            <div className="min-w-0 flex-1">
                                <p className="m-0 text-[12px] font-bold uppercase tracking-[0.08em] text-[var(--v2-text-muted)]">
                                    Ordered test
                                </p>
                                <h4 className="m-0 truncate text-[18px] font-bold text-[var(--v2-text-primary)]">
                                    {testName}
                                </h4>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 xl:grid-cols-2">
                            {/* ── SPECIMEN ── */}
                            <div
                                className={`border-b border-[var(--v2-border-subtle)] px-5 py-4 xl:border-b-0 ${sample || testResults.length > 0 ? 'xl:border-r' : 'xl:col-span-2'} xl:border-[var(--v2-border-subtle)]`}
                            >
                                <div className="mb-2.5 flex items-center gap-1.5">
                                    <FlaskConical
                                        className="h-3.5 w-3.5"
                                        style={{ color: 'var(--v2-primary-500)' }}
                                        strokeWidth={2}
                                    />
                                    <h5 className="m-0 text-[13px] font-bold uppercase tracking-[0.08em] text-[var(--v2-text-secondary)]">
                                        Specimen
                                    </h5>
                                </div>

                                {!sample ? (
                                    <div className="flex items-center justify-between gap-3">
                                        <p className="m-0 text-[13px] font-medium text-[var(--v2-text-muted)]">
                                            No specimen registered yet.
                                        </p>
                                        {canCollectSamples && (
                                            <Button
                                                variant="primary"
                                                size="small"
                                                onClick={() => setRegisterTarget({ testId, testName })}
                                                leftIcon={
                                                    <Plus
                                                        className="h-full w-full"
                                                        strokeWidth={2.5}
                                                    />
                                                }
                                            >
                                                Register Specimen
                                            </Button>
                                        )}
                                    </div>
                                ) : (
                                    <div className="flex flex-col gap-2">
                                        <div className="flex items-start gap-3">
                                            <div className="flex min-w-0 items-center gap-2.5">
                                                <div
                                                    className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border"
                                                    style={{
                                                        background: SAMPLE_STATUS_META[sampleStatus!].bg,
                                                        borderColor: `${SAMPLE_STATUS_META[sampleStatus!].color}30`,
                                                    }}
                                                >
                                                    <FlaskConical
                                                        className="h-4 w-4"
                                                        strokeWidth={1.5}
                                                        style={{ color: SAMPLE_STATUS_META[sampleStatus!].color }}
                                                    />
                                                </div>
                                                <div className="flex min-w-0 flex-col gap-0.5">
                                                    <div className="flex flex-wrap items-center gap-2">
                                                        <h5 className="m-0 text-[15px] font-bold text-[var(--v2-text-heading)]">
                                                            {sample.sampleTypeName ??
                                                                (sample as any).sample_type_name ??
                                                                'Sample'}
                                                        </h5>
                                                        <span
                                                            className="rounded-full px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide"
                                                            style={{
                                                                background: SAMPLE_STATUS_META[sampleStatus!].color,
                                                                color: 'white',
                                                            }}
                                                        >
                                                            {SAMPLE_STATUS_META[sampleStatus!].label}
                                                        </span>
                                                        {sample.barcodeNumber && (
                                                            <span className="flex items-center gap-1 rounded border border-[var(--v2-border-subtle)] bg-[var(--v2-bg-surface)] px-1.5 py-0.5">
                                                                <Barcode className="h-3 w-3 text-[var(--v2-text-muted)]" />
                                                                <span className="font-mono text-[12px] font-bold text-[var(--v2-text-primary)]">
                                                                    #{sample.barcodeNumber}
                                                                </span>
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="flex flex-wrap items-center gap-3 text-[13px] font-medium text-[var(--v2-text-secondary)]">
                                                        {(sample.bottleType ?? (sample as any).bottle_type) && (
                                                            <span>
                                                                <span className="text-[var(--v2-text-muted)]">Tube:</span>{' '}
                                                                {sample.bottleType ?? (sample as any).bottle_type}
                                                            </span>
                                                        )}
                                                        {(sample.collectedBy ?? (sample as any).collected_by) && (
                                                            <span className="flex items-center gap-1">
                                                                <User2
                                                                    className="h-3 w-3 text-[var(--v2-text-muted)]"
                                                                    strokeWidth={2}
                                                                />
                                                                <span className="text-[var(--v2-text-muted)]">Collected by:</span>
                                                                {sample.collectedBy ?? (sample as any).collected_by}
                                                            </span>
                                                        )}
                                                        {(sample.collectedDate ?? sample.createdAt) && (
                                                            <span className="flex items-center gap-1">
                                                                <CalendarDays
                                                                    className="h-3 w-3 text-[var(--v2-text-muted)]"
                                                                    strokeWidth={2}
                                                                />
                                                                <span className="text-[var(--v2-text-muted)]">Collected:</span>
                                                                {formatLabDate(sample.collectedDate ?? sample.createdAt)}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {sampleStatus === 'rejected' && sample.rejectionReason && (
                                            <div className="flex items-start gap-1.5 rounded-md border border-red-100 bg-red-50 px-2.5 py-1.5">
                                                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-red-600" />
                                                <p className="m-0 text-[13px] font-semibold text-red-700">
                                                    Rejected: {sample.rejectionReason}
                                                </p>
                                            </div>
                                        )}

                                        <div className="mt-1 flex flex-wrap items-center justify-end gap-2 border-t border-[var(--v2-border-subtle)] pt-3">
                                            {canCollectSamples && nextSampleStatus && (
                                                <Button
                                                    variant="primary"
                                                    size="small"
                                                    onClick={() => advanceStatus(sample)}
                                                    loading={toggling === sId}
                                                    leftIcon={
                                                        toggling !== sId ? (
                                                            <AdvanceIcon
                                                                className="h-full w-full"
                                                                strokeWidth={2}
                                                            />
                                                        ) : undefined
                                                    }
                                                >
                                                    {ADVANCE_LABEL[sampleStatus!]}
                                                </Button>
                                            )}

                                            <Dropdown
                                                isOpen={sampleActionsOpen === sId}
                                                toggleDropdown={() =>
                                                    setSampleActionsOpen(current => (current === sId ? null : sId))
                                                }
                                                onClose={() => setSampleActionsOpen(null)}
                                                placement="bottom-right"
                                                actionComponent={
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            setSampleActionsOpen(current => (current === sId ? null : sId))
                                                        }
                                                        className="flex h-9 items-center gap-1.5 rounded-lg border border-[var(--v2-border-card)] bg-[var(--v2-bg-card)] px-3 text-[13px] font-semibold text-[var(--v2-text-secondary)] hover:bg-[var(--v2-bg-hover)]"
                                                        aria-label="More specimen actions"
                                                        aria-expanded={sampleActionsOpen === sId}
                                                    >
                                                        <MoreHorizontal className="h-4 w-4" />
                                                        More
                                                    </button>
                                                }
                                                options={[
                                                    {
                                                        value: 'barcode',
                                                        label: 'View barcode',
                                                        icon: <Barcode className="h-4 w-4" />,
                                                        onClick: () => setBarcodeTarget(sId),
                                                    },
                                                    ...(canCollectSamples && sampleStatus !== 'rejected'
                                                        ? [
                                                            {
                                                                value: 'reject',
                                                                label: 'Reject specimen',
                                                                icon: <Ban className="h-4 w-4" />,
                                                                onClick: () => setRejectSampleTarget(sId),
                                                            },
                                                        ]
                                                        : []),
                                                    ...(canCollectSamples
                                                        ? [
                                                            {
                                                                value: 'delete',
                                                                label: 'Delete specimen',
                                                                icon: <Trash2 className="h-4 w-4 text-red-500" />,
                                                                separator: true,
                                                                onClick: () => setDeleteTarget(sample),
                                                            },
                                                        ]
                                                        : []),
                                                ]}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>

                            {(sample || testResults.length > 0) && (
                                <div className="px-5 py-4">
                                    <div className="mb-2.5 flex items-center justify-between gap-3">
                                        <div className="flex items-center gap-1.5">
                                            <ClipboardList
                                                className="h-3.5 w-3.5"
                                                style={{ color: 'var(--v2-primary-500)' }}
                                                strokeWidth={2}
                                            />
                                            <h5 className="m-0 text-[13px] font-bold uppercase tracking-[0.08em] text-[var(--v2-text-secondary)]">
                                                Results
                                                {testResults.length > 0 && (
                                                    <span className="normal-case opacity-60"> · {testResults.length}</span>
                                                )}
                                            </h5>
                                        </div>
                                        {canEnterResultNow && testResults.length > 0 && (
                                            <Button
                                                variant="outline"
                                                size="small"
                                                onClick={() => openResultModal(null)}
                                                leftIcon={
                                                    <Plus
                                                        className="h-full w-full"
                                                        strokeWidth={2.5}
                                                    />
                                                }
                                                className="!border-[var(--v2-primary-400,rgba(4,94,184,0.35))] !text-[var(--v2-primary-600,#045eb8)]"
                                            >
                                                New Result
                                            </Button>
                                        )}
                                    </div>

                                    {loadingResults ? (
                                        <div className="flex items-center justify-center gap-2.5 py-6">
                                            <div className="h-4 w-4 animate-spin rounded-full border-2 border-[var(--v2-primary-500)] border-t-transparent" />
                                            <span className="text-[12px] font-medium text-[var(--v2-text-muted)]">
                                                Loading results…
                                            </span>
                                        </div>
                                    ) : testResults.length === 0 ? (
                                        <div className="flex min-h-[112px] items-center gap-4 rounded-lg bg-[var(--v2-bg-surface)] p-4">
                                            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full border border-[var(--v2-border-card)] bg-[var(--v2-bg-card)]">
                                                <ClipboardList
                                                    className="w-4.5 h-4.5 text-[var(--v2-text-muted)] opacity-60"
                                                    strokeWidth={1.5}
                                                />
                                            </div>
                                            <div className="min-w-0 flex-1 text-left">
                                                <p className="m-0 text-[15px] font-semibold text-[var(--v2-text-heading)]">
                                                    No result entered
                                                </p>
                                                <p className="m-0 mt-1 text-[13px] text-[var(--v2-text-secondary)]">
                                                    Add findings when the analysis is ready.
                                                </p>
                                            </div>
                                            {canEnterResultNow && (
                                                <Button
                                                    variant="primary"
                                                    size="small"
                                                    onClick={() => openResultModal(null)}
                                                    leftIcon={
                                                        <Plus
                                                            className="h-full w-full"
                                                            strokeWidth={2.5}
                                                        />
                                                    }
                                                    className="shrink-0"
                                                >
                                                    Enter Result
                                                </Button>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="flex flex-col gap-2">
                                            {testResults.map((r, ri) => {
                                                const rId = extractLabResultId(r);
                                                const rStatus = normalizeResultStatus((r as any).reportStatus ?? r.status);
                                                const rMeta = RESULT_STATUS_META[rStatus];
                                                const needsVerification =
                                                    rStatus === 'draft' && canVerifyResults && !r.verifiedBy;
                                                const canApproveThis = rStatus === 'pending_approval' && canApproveReports;
                                                const verifyBusy = resultActionBusy === `${rId}:verify`;
                                                const submitBusy = resultActionBusy === `${rId}:submit`;
                                                const approveBusy = resultActionBusy === `${rId}:approve`;

                                                const displayColor = needsVerification ? '#D97706' : rMeta.color;
                                                const displayBg = needsVerification ? 'rgba(217,119,6,0.1)' : rMeta.bg;
                                                const displayLabel = needsVerification
                                                    ? 'Verification Required'
                                                    : rMeta.label;
                                                const hasActions =
                                                    needsVerification || rStatus === 'draft' || canApproveThis;

                                                return (
                                                    <div
                                                        key={rId || ri}
                                                        className="relative overflow-hidden rounded-lg border border-[var(--v2-border-card)] bg-[var(--v2-bg-card)]"
                                                    >
                                                        <div
                                                            className="absolute left-0 top-0 bottom-0 w-1"
                                                            style={{ background: displayColor }}
                                                        />
                                                        <div className="flex items-center gap-3 py-3 pl-4 pr-3">
                                                            <div
                                                                className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border"
                                                                style={{ background: displayBg, borderColor: `${displayColor}30` }}
                                                            >
                                                                <ClipboardList
                                                                    className="h-4 w-4"
                                                                    strokeWidth={1.5}
                                                                    style={{ color: displayColor }}
                                                                />
                                                            </div>
                                                            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                                                                <div className="flex flex-wrap items-center gap-2">
                                                                    <h5 className="m-0 text-[12.5px] font-bold text-[var(--v2-text-heading)]">
                                                                        {testResults.length > 1 ? `Result ${ri + 1}` : 'Result'}
                                                                    </h5>
                                                                    <span
                                                                        className="rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide"
                                                                        style={{ background: displayBg, color: displayColor }}
                                                                    >
                                                                        {displayLabel}
                                                                    </span>
                                                                </div>
                                                                <div className="flex flex-wrap items-center gap-3 text-[10.5px] font-medium text-[var(--v2-text-secondary)]">
                                                                    {r.enteredBy && <span>Entered by {r.enteredBy}</span>}
                                                                    {r.verifiedBy && (
                                                                        <span className="font-bold text-emerald-600">
                                                                            Verified by {r.verifiedBy}
                                                                        </span>
                                                                    )}
                                                                    {r.updatedAt && (
                                                                        <span className="flex items-center gap-1">
                                                                            <CalendarDays
                                                                                className="h-3 w-3"
                                                                                strokeWidth={2}
                                                                            />
                                                                            {formatLabDate(r.updatedAt)}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div
                                                            className={`flex flex-wrap items-center justify-end gap-1.5 px-3 py-2.5 ${hasActions ? 'border-t border-[var(--v2-border-subtle)] bg-[var(--v2-bg-surface)]' : ''}`}
                                                        >
                                                            {needsVerification && (
                                                                <Button
                                                                    variant="outline"
                                                                    size="small"
                                                                    onClick={() => handleVerifyResult(r)}
                                                                    loading={verifyBusy}
                                                                    className="!border-amber-300 !bg-amber-100 !text-amber-800 hover:!bg-amber-200"
                                                                    leftIcon={
                                                                        !verifyBusy ? (
                                                                            <CheckCircle2
                                                                                className="h-full w-full"
                                                                                strokeWidth={2}
                                                                            />
                                                                        ) : undefined
                                                                    }
                                                                >
                                                                    Verify
                                                                </Button>
                                                            )}
                                                            {rStatus === 'draft' && (
                                                                <Button
                                                                    variant="primary"
                                                                    size="small"
                                                                    onClick={() => handleSubmitResult(r)}
                                                                    loading={submitBusy}
                                                                    leftIcon={
                                                                        !submitBusy ? (
                                                                            <Send
                                                                                className="h-full w-full"
                                                                                strokeWidth={2}
                                                                            />
                                                                        ) : undefined
                                                                    }
                                                                >
                                                                    Submit
                                                                </Button>
                                                            )}
                                                            {canApproveThis && (
                                                                <>
                                                                    <Button
                                                                        variant="outline"
                                                                        size="small"
                                                                        onClick={() => setRejectResultTarget(rId)}
                                                                        className="!border-red-200 !bg-red-50 !text-red-600 hover:!bg-red-600 hover:!text-white"
                                                                        leftIcon={
                                                                            <XCircle
                                                                                className="h-full w-full"
                                                                                strokeWidth={2}
                                                                            />
                                                                        }
                                                                    >
                                                                        Reject
                                                                    </Button>
                                                                    <Button
                                                                        variant="primary"
                                                                        size="small"
                                                                        onClick={() => handleApproveResult(r)}
                                                                        loading={approveBusy}
                                                                        style={{
                                                                            background: 'linear-gradient(to bottom, #10b981, #059669)',
                                                                        }}
                                                                        leftIcon={
                                                                            !approveBusy ? (
                                                                                <CheckCircle2
                                                                                    className="h-full w-full"
                                                                                    strokeWidth={2.5}
                                                                                />
                                                                            ) : undefined
                                                                        }
                                                                    >
                                                                        Approve
                                                                    </Button>
                                                                </>
                                                            )}
                                                            <Button
                                                                variant="outline"
                                                                size="small"
                                                                onClick={() => openResultModal(r)}
                                                                leftIcon={
                                                                    <ClipboardList
                                                                        className="h-full w-full"
                                                                        strokeWidth={2}
                                                                    />
                                                                }
                                                            >
                                                                {rStatus === 'approved' || rStatus === 'rejected'
                                                                    ? 'View'
                                                                    : 'View / Edit'}
                                                            </Button>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}

            {/* Modals */}
            {registerTarget && (
                <RegisterSpecimenModal
                    orderId={orderId}
                    testId={registerTarget.testId}
                    testName={registerTarget.testName}
                    sampleTypes={sampleTypes}
                    onClose={() => setRegisterTarget(null)}
                    onSuccess={refreshAll}
                />
            )}
            {resultTarget && (
                <ResultEntryModal
                    orderId={orderId}
                    order={order}
                    testId={resultTarget.testId}
                    testName={resultTarget.testName}
                    existingResult={
                        resultTarget.resultId
                            ? (results.find(r => extractLabResultId(r) === resultTarget.resultId) ?? null)
                            : null
                    }
                    samples={samples}
                    onClose={() => setResultTarget(null)}
                    onChange={refreshAll}
                />
            )}
            {barcodeTarget && (
                <BarcodeModal
                    sampleId={barcodeTarget}
                    onClose={() => setBarcodeTarget(null)}
                />
            )}
            {showEmailModal && (
                <EmailModal
                    orderId={orderId}
                    onClose={() => setShowEmailModal(false)}
                />
            )}
            {rejectSampleTarget && (
                <RejectSampleModal
                    rejecting={rejectingSample}
                    onConfirm={confirmRejectSample}
                    onClose={() => setRejectSampleTarget(null)}
                />
            )}
            {rejectResultTarget && (
                <RejectResultModal
                    rejecting={rejectingResult}
                    onConfirm={confirmRejectResult}
                    onClose={() => setRejectResultTarget(null)}
                />
            )}
            {deleteTarget && (
                <Dialog
                    isOpen
                    onClose={() => setDeleteTarget(null)}
                    size="small"
                    preventCloseOnOverlayClick
                    showCloseButton={false}
                >
                    <div className="-mt-2 flex flex-col gap-4">
                        <div className="flex items-center gap-3">
                            <div
                                className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl"
                                style={{ background: 'rgba(239,68,68,0.1)' }}
                            >
                                <AlertTriangle
                                    className="h-5 w-5 text-red-600"
                                    strokeWidth={2.5}
                                />
                            </div>
                            <div>
                                <h3 className="m-0 text-[15px] font-bold text-[var(--v2-text-heading)]">
                                    Delete Sample?
                                </h3>
                                <p className="m-0 text-[12px] font-medium text-[var(--v2-text-secondary)]">
                                    This action cannot be undone.
                                </p>
                            </div>
                        </div>
                        <div className="flex gap-3">
                            <Button
                                variant="outline"
                                fullWidth
                                onClick={() => setDeleteTarget(null)}
                            >
                                Keep Sample
                            </Button>
                            <Button
                                variant="danger"
                                fullWidth
                                onClick={confirmDelete}
                            >
                                Yes, Delete
                            </Button>
                        </div>
                    </div>
                </Dialog>
            )}
        </div>
    );
};

export default OrderWorkflowList;
